import { api } from './axios';

export interface AppUserRow {
  id: number;
  username: string;
  roleName: string;
  roleId: number;
  staffId: number | null;
  active: boolean;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
  createdAt: string;
}

export interface AppRole {
  id: number;
  name: string;
  description: string | null;
  system: boolean;
  active: boolean;
}

export interface AppModuleDto {
  id: number;
  code: string;
  name: string;
  sortOrder: number;
}

export type PermissionAction = 'VIEW' | 'CREATE' | 'EDIT' | 'APPROVE' | 'DELETE';
export const PERMISSION_ACTIONS: PermissionAction[] = ['VIEW', 'CREATE', 'EDIT', 'APPROVE', 'DELETE'];

export interface RolePermissions {
  roleId: number;
  /** moduleCode → granted actions */
  grants: Record<string, PermissionAction[]>;
}

export interface UsersPage {
  rows: AppUserRow[];
  page: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export interface UsersListParams {
  q?: string;
  roleId?: number;
  active?: boolean;
  page?: number;
  size?: number;
}

export interface CreateUserBody {
  username: string;
  password: string;
  roleId: number;
  staffId?: number | null;
}

export interface CreateUserFromStaffBody {
  staffId: number;
  username: string;
  roleId: number;
}

export interface UpdateUserBody {
  roleId: number;
  staffId?: number | null;
  active: boolean;
}

interface SpringPage<T> {
  content: T[];
  number: number;
  size: number;
  totalElements: number;
  totalPages: number;
}

export const usersApi = {
  list: (params: UsersListParams = {}): Promise<UsersPage> =>
    api
      .get<SpringPage<AppUserRow>>('/users', {
        params: {
          q: params.q || undefined,
          roleId: params.roleId,
          active: params.active,
          page: params.page ?? 0,
          size: params.size ?? 25,
        },
      })
      .then((r) => ({
        rows: r.data.content,
        page: r.data.number,
        size: r.data.size,
        totalElements: r.data.totalElements,
        totalPages: r.data.totalPages,
      })),

  create: (body: CreateUserBody) =>
    api.post<AppUserRow>('/users', body).then((r) => r.data),

  createFromStaff: (body: CreateUserFromStaffBody) =>
    api.post<AppUserRow>('/users/from-staff', body).then((r) => r.data),

  update: (id: number, body: UpdateUserBody) =>
    api.put<AppUserRow>(`/users/${id}`, body).then((r) => r.data),

  resetPassword: (id: number, newPassword: string) =>
    api.post<AppUserRow>(`/users/${id}/reset-password`, { newPassword }).then((r) => r.data),

  setActive: (id: number, value: boolean) =>
    api
      .patch<AppUserRow>(`/users/${id}/active`, null, { params: { value } })
      .then((r) => r.data),
};

export interface CapabilityDef {
  code: string;
  moduleCode: string;
  label: string;
  description: string;
}

export interface RoleCapabilities {
  roleId: number;
  codes: string[];
}

export const rolesApi = {
  list: () => api.get<AppRole[]>('/roles').then((r) => r.data),
  modules: () => api.get<AppModuleDto[]>('/roles/modules').then((r) => r.data),
  permissions: (roleId: number) =>
    api.get<RolePermissions>(`/roles/${roleId}/permissions`).then((r) => r.data),
  updatePermissions: (roleId: number, grants: Record<string, PermissionAction[]>) =>
    api
      .put<RolePermissions>(`/roles/${roleId}/permissions`, { grants })
      .then((r) => r.data),

  capabilityCatalog: () =>
    api.get<CapabilityDef[]>('/roles/capabilities/catalog').then((r) => r.data),
  capabilities: (roleId: number) =>
    api.get<RoleCapabilities>(`/roles/${roleId}/capabilities`).then((r) => r.data),
  updateCapabilities: (roleId: number, codes: string[]) =>
    api.put<RoleCapabilities>(`/roles/${roleId}/capabilities`, { codes }).then((r) => r.data),
};
